import type { CallbackTicket } from '../services/callbackTicketService';
import type { TakeoverStateName } from '../services/conversationService';
import type {
  ActiveChat,
  LiveCallSummary,
  LiveOverview,
  WaitingContact,
} from '../services/liveOverviewService';

/**
 * Pure updates of the live overview (`GET /api/live/overview`) from the
 * event stream (contract livecenter §3–§5, rueckruf-ticket §5).
 *
 * Rule: apply locally only what the event fully describes. When an event
 * refers to something the overview does not know (a new waiting contact
 * whose name and reply window are not in a `whatsapp.takeover`, a step of a
 * call whose start was missed) the result asks for a refetch instead of
 * showing a half-filled entry.
 */

/** Extended takeover state as sent in events (contract livecenter §4). */
export interface LiveTakeover {
  active: boolean;
  state?: TakeoverStateName | null;
  taken_by?: string | null;
  started_at?: string | null;
  expires_at?: string | null;
  deadline_at?: string | null;
  deadline_kind?: string | null;
  requested_at?: string | null;
  request_reason?: string | null;
  request_summary?: string | null;
  callback_at?: string | null;
  last_staff_at?: string | null;
  last_customer_at?: string | null;
  customer_waiting_since?: string | null;
}

export interface WhatsappTakeoverEvent {
  contact_key?: string | null;
  conversation_id?: number | null;
  customer_phone?: string | null;
  agent_number?: string | null;
  takeover: LiveTakeover | null;
  reason?: string | null;
}

export interface WhatsappMessageEvent {
  contact_key?: string | null;
  conversation_id?: number | null;
  customer_phone?: string | null;
  customer_name?: string | null;
  agent_number?: string | null;
  message?: { role: string; content?: string | null; ts?: string | null } | null;
  last_message_at?: string | null;
  is_test?: boolean;
  /** Absent = the event says nothing about the takeover; null = none active. */
  takeover?: LiveTakeover | null;
}

export interface CallLiveEvent {
  call_sid: string;
  status: 'started' | 'transferring' | 'ended';
  customer_phone: string | null;
  customer_name: string | null;
  agent_number: string | null;
  started_at: string | null;
  ended_at: string | null;
  conversation_id: number | null;
}

export interface CallStepEvent {
  call_sid: string;
  label: string;
  tool?: string | null;
  ts?: string | null;
}

export interface OverviewUpdate {
  overview: LiveOverview;
  /** The event could not be applied safely — reload the overview. */
  refetch: boolean;
}

export const ACTIVE_CHAT_WINDOW_MS = 10 * 60_000;
export const MAX_ACTIVE_CHATS = 10;
/** Meta's customer-service window: 24 h after the customer's last message. */
export const REPLY_WINDOW_MS = 24 * 3_600_000;

const WAITING_ORDER: Record<TakeoverStateName, number> = { requested: 0, staff: 1, callback: 2 };

const unchanged = (overview: LiveOverview, refetch = false): OverviewUpdate => ({ overview, refetch });

function time(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** Live requests first, then customers waiting in a taken-over chat, then callbacks; longest waiting first. */
export function sortWaiting(entries: WaitingContact[]): WaitingContact[] {
  return [...entries].sort(
    (a, b) =>
      (WAITING_ORDER[a.state] ?? 3) - (WAITING_ORDER[b.state] ?? 3) || time(a.waiting_since) - time(b.waiting_since),
  );
}

/** Since when the contact waits for the salon, or null when it does not (contract §5 `waiting`). */
export function waitingSince(takeover: LiveTakeover | null | undefined): string | null {
  if (!takeover || !takeover.active) return null;
  switch (takeover.state ?? 'staff') {
    case 'requested':
      return takeover.requested_at ?? takeover.started_at ?? null;
    case 'callback':
      return takeover.callback_at ?? takeover.started_at ?? null;
    case 'staff':
      return takeover.customer_waiting_since ?? null;
    default:
      return null;
  }
}

function updateWaitingEntry(entry: WaitingContact, takeover: LiveTakeover, since: string): WaitingContact {
  const state = takeover.state ?? 'staff';
  return {
    ...entry,
    state,
    waiting_since: since,
    deadline_at: state === 'callback' ? null : takeover.deadline_at ?? null,
    reason: takeover.request_reason ?? entry.reason,
    summary: takeover.request_summary ?? entry.summary,
  };
}

function withWaiting(overview: LiveOverview, waiting: WaitingContact[]): LiveOverview {
  return { ...overview, waiting: sortWaiting(waiting) };
}

export function applyWhatsappTakeover(overview: LiveOverview, event: WhatsappTakeoverEvent): OverviewUpdate {
  const key = event?.contact_key;
  if (!key) return unchanged(overview, true);
  const existing = overview.waiting.find((w) => w.contact_key === key);
  const since = waitingSince(event.takeover);
  if (!since || !event.takeover) {
    if (!existing) return unchanged(overview);
    return unchanged(withWaiting(overview, overview.waiting.filter((w) => w.contact_key !== key)));
  }
  if (!existing) return unchanged(overview, true);
  const updated = updateWaitingEntry(existing, event.takeover, since);
  return unchanged(withWaiting(overview, overview.waiting.map((w) => (w.contact_key === key ? updated : w))));
}

function upsertActiveChat(chats: ActiveChat[], chat: ActiveChat): ActiveChat[] {
  return [chat, ...chats.filter((c) => c.contact_key !== chat.contact_key)].slice(0, MAX_ACTIVE_CHATS);
}

export function applyWhatsappMessage(overview: LiveOverview, event: WhatsappMessageEvent, now: number): OverviewUpdate {
  const key = event?.contact_key;
  if (!key) return unchanged(overview);
  const isCustomer = event.message?.role === 'user';
  const ts = event.message?.ts ?? event.last_message_at ?? new Date(now).toISOString();
  const windowUntil = isCustomer ? new Date(time(ts) + REPLY_WINDOW_MS).toISOString() : null;
  let next = overview;
  let refetch = false;

  if (isCustomer && !event.is_test) {
    next = {
      ...next,
      active_chats: upsertActiveChat(next.active_chats, {
        contact_key: key,
        customer_phone: event.customer_phone ?? '',
        customer_name: event.customer_name ?? null,
        last_message_at: ts,
        preview: event.message?.content ?? null,
        conversation_id: event.conversation_id ?? null,
      }),
    };
  }

  const existing = next.waiting.find((w) => w.contact_key === key);
  let waiting = next.waiting;
  if (existing && windowUntil) {
    waiting = waiting.map((w) =>
      w.contact_key === key
        ? { ...w, reply_window_until: windowUntil, customer_name: event.customer_name ?? w.customer_name }
        : w,
    );
  }

  if (event.takeover !== undefined) {
    const since = waitingSince(event.takeover);
    const current = waiting.find((w) => w.contact_key === key);
    if (!since || !event.takeover) {
      waiting = waiting.filter((w) => w.contact_key !== key);
    } else if (current) {
      waiting = waiting.map((w) => (w.contact_key === key ? updateWaitingEntry(w, event.takeover!, since) : w));
    } else if (windowUntil && event.customer_phone) {
      const state = event.takeover.state ?? 'staff';
      waiting = [
        ...waiting,
        {
          contact_key: key,
          customer_phone: event.customer_phone,
          customer_name: event.customer_name ?? null,
          agent_number: event.agent_number ?? null,
          state,
          waiting_since: since,
          reason: event.takeover.request_reason ?? null,
          summary: event.takeover.request_summary ?? null,
          reply_window_until: windowUntil,
          conversation_id: event.conversation_id ?? null,
          deadline_at: state === 'callback' ? null : event.takeover.deadline_at ?? null,
        },
      ];
    } else {
      refetch = true;
    }
  }

  if (waiting !== next.waiting) next = withWaiting(next, waiting);
  return { overview: next, refetch };
}

export function pruneActiveChats(overview: LiveOverview, now: number): LiveOverview {
  const kept = overview.active_chats.filter((c) => now - time(c.last_message_at) < ACTIVE_CHAT_WINDOW_MS);
  return kept.length === overview.active_chats.length ? overview : { ...overview, active_chats: kept };
}

/** Ms until the oldest active chat leaves the 10-minute window, or null without active chats. */
export function nextActiveChatExpiry(overview: LiveOverview, now: number): number | null {
  if (overview.active_chats.length === 0) return null;
  const oldest = Math.min(...overview.active_chats.map((c) => time(c.last_message_at)));
  return Math.max(0, oldest + ACTIVE_CHAT_WINDOW_MS - now);
}

export function applyCallLive(overview: LiveOverview, event: CallLiveEvent): OverviewUpdate {
  if (!event?.call_sid) return unchanged(overview);
  const others = overview.live_calls.filter((c) => c.call_sid !== event.call_sid);
  if (event.status === 'ended') {
    return others.length === overview.live_calls.length
      ? unchanged(overview)
      : unchanged({ ...overview, live_calls: others });
  }
  if (event.status !== 'started' && event.status !== 'transferring') return unchanged(overview, true);
  const existing = overview.live_calls.find((c) => c.call_sid === event.call_sid);
  const call: LiveCallSummary = {
    call_sid: event.call_sid,
    customer_phone: event.customer_phone ?? existing?.customer_phone ?? '',
    customer_name: event.customer_name ?? existing?.customer_name ?? null,
    agent_number: event.agent_number ?? existing?.agent_number ?? null,
    started_at: event.started_at ?? existing?.started_at ?? new Date().toISOString(),
    status: event.status === 'transferring' ? 'transferring' : 'live',
    last_step: existing?.last_step ?? null,
  };
  const calls = existing
    ? overview.live_calls.map((c) => (c.call_sid === call.call_sid ? call : c))
    : [call, ...overview.live_calls];
  return unchanged({ ...overview, live_calls: calls });
}

export function applyCallStep(overview: LiveOverview, event: CallStepEvent): OverviewUpdate {
  if (!event?.call_sid) return unchanged(overview);
  if (!overview.live_calls.some((c) => c.call_sid === event.call_sid)) return unchanged(overview, true);
  return unchanged({
    ...overview,
    live_calls: overview.live_calls.map((c) =>
      c.call_sid === event.call_sid ? { ...c, last_step: event.label || c.last_step } : c,
    ),
  });
}

/** Open tickets oldest first, as the server lists them. */
export function sortCallbackTickets(tickets: CallbackTicket[]): CallbackTicket[] {
  return [...tickets].sort((a, b) => time(a.created_at) - time(b.created_at) || a.id - b.id);
}

/**
 * `callback_ticket.upsert` (and the answer of our own „Erledigt“). A done
 * ticket leaves the list; a listed open one is updated in place. A new open
 * one is reloaded instead of added: the event does not say whether it came
 * from a test call (the overview leaves those out), and the overview fills a
 * missing name from the customer's conversations.
 */
export function applyCallbackTicket(overview: LiveOverview, ticket: CallbackTicket): OverviewUpdate {
  if (!ticket || typeof ticket.id !== 'number') return unchanged(overview);
  const existing = overview.callback_tickets.find((t) => t.id === ticket.id);
  if (ticket.status !== 'open') {
    if (!existing) return unchanged(overview);
    return unchanged({ ...overview, callback_tickets: overview.callback_tickets.filter((t) => t.id !== ticket.id) });
  }
  if (!existing) return unchanged(overview, true);
  if (time(ticket.updated_at) < time(existing.updated_at)) return unchanged(overview);
  const updated: CallbackTicket = {
    ...ticket,
    customer_name: ticket.customer_name ?? existing.customer_name,
    conversation_id: ticket.conversation_id ?? existing.conversation_id,
  };
  return unchanged({
    ...overview,
    callback_tickets: sortCallbackTickets(overview.callback_tickets.map((t) => (t.id === ticket.id ? updated : t))),
  });
}
